// This rule is selected per request, never a saved preference or the default split mode.
export const ONE_CLICK_FLOW_RULE_ID = 'one_click_flow_v1';

export const isOneClickFlowSegment = (segment) => (
  segment?.storyboardRuleId === ONE_CLICK_FLOW_RULE_ID
  || segment?.spatial?.storyboardRuleId === ONE_CLICK_FLOW_RULE_ID
);

export const supportsOneClickFlowRule = (capabilities) => (
  Array.isArray(capabilities?.supportedStoryboardRuleIds)
  && capabilities.supportedStoryboardRuleIds.includes(ONE_CLICK_FLOW_RULE_ID)
);

export const isOneClickFlowGenerationResult = (result) => (
  result?.storyboardRuleId === ONE_CLICK_FLOW_RULE_ID
  && Array.isArray(result?.segments)
  && result.segments.length > 0
  && result.segments.every(isOneClickFlowSegment)
);

export const buildOneClickFlowSplitPayload = ({
  projectId,
  episodeId,
  episodeTitle,
  script,
  aspectRatio,
  assetNames,
  videoProvider,
  videoModel,
  defaultLightingPrompt,
}) => ({
  projectId,
  episodeId,
  episodeTitle,
  script,
  aspectRatio,
  assetNames,
  videoProvider,
  videoModel,
  storyboardRuleId: ONE_CLICK_FLOW_RULE_ID,
  ...(defaultLightingPrompt !== undefined ? { defaultLightingPrompt: String(defaultLightingPrompt ?? '') } : {}),
});

const oneClickFlowBudgetSeconds = (segment) => {
  const savedBudget = Number(segment?.spatial?.plannedDurationSeconds);
  const shotBudget = (Array.isArray(segment?.shots) ? segment.shots : [])
    .reduce((sum, shot) => sum + Math.max(0, Number(shot?.durationSeconds) || 0), 0);
  return savedBudget > 0 ? savedBudget : shotBudget;
};

export const getOneClickFlowTimingLabel = (segment, availableDurationSeconds) => {
  if (!isOneClickFlowSegment(segment)) return '';
  const budget = oneClickFlowBudgetSeconds(segment);
  const available = Number(availableDurationSeconds);
  if (!(budget > 0) || !(available > 0)) return '';
  return `镜头预估 ${budget.toFixed(1)}s / 视频生成 ${available}s`;
};

// Validate against today's actual outbound duration (including a provider's cap),
// not a previously saved error message from a different model. This does not
// change the independent card duration or any decimal shot timing.
export const getOneClickFlowDurationError = (segment, availableDurationSeconds) => {
  if (!isOneClickFlowSegment(segment)) return '';
  const budget = oneClickFlowBudgetSeconds(segment);
  const available = Number(availableDurationSeconds);
  if (!(budget > 0) || !(available > 0) || budget <= available + 0.0001) return '';
  return `一键流分镜的镜头预计需要 ${Number(budget.toFixed(1))} 秒，当前线路和时长设置只可生成 ${available} 秒。请增加时长或拆分片段后再生成；完整提示词已保存，不会自动合并或截断镜头。`;
};

// The one-click rule embeds dialogue in the visual paragraph. Keep both spoken text
// and 【voice directions】 intact while the existing binding engine replaces names
// outside them. The old standalone-dialogue format continues using its old engine.
export const transformOneClickFlowPrompt = (prompt, transform) => {
  const source = String(prompt ?? '');
  let tokenPrefix = '__ONE_CLICK_FLOW_PROTECTED_';
  while (source.includes(tokenPrefix)) tokenPrefix += '_';
  const protectedText = [];
  const masked = source.replace(/“[^”]*(?:”|$)|"[^"\\]*(?:\\.[^"\\]*)*(?:"|$)|【[^】]*(?:】|$)/g, (text) => {
    const token = `${tokenPrefix}${protectedText.length}__`;
    protectedText.push({ token, text });
    return token;
  });
  let result = transform(masked);
  protectedText.forEach(({ token, text }) => {
    result = result.replaceAll(token, () => text);
  });
  return result;
};
